import React, { useState, useEffect, useRef } from 'react';
import { OpportunityStatus, TechnicalStatus } from '../models/opportunity';

export interface NovaOportunidadeForm {
  titulo: string;
  cliente: string;
  contactoCliente: string;
  comercialEmail: string;
  comercialNome: string;
  statusOportunidade: OpportunityStatus;
  ambitoNecessidades: string;
  tecnicoEmail: string;
  tecnicoNome: string;
  statusTecnico: TechnicalStatus;
  dataLimiteParecer: string;
  valorEstimado?: number;
}

interface Membro { nome: string; email: string; }

interface Props {
  currentUserEmail: string;
  currentUserName: string;
  onSubmit: (form: NovaOportunidadeForm) => Promise<void>;
  onClose: () => void;
  getMembers: () => Promise<Membro[]>;
  getNextCode: () => Promise<string>;
}

const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0D0F1A', border: '1px solid #252B45',
  borderRadius: 8, padding: '9px 12px', color: '#D8DDEF', fontSize: 13,
  fontFamily: 'inherit', outline: 'none',
};
const labelStyle: React.CSSProperties = {
  fontSize: 11, color: '#8892AA', textTransform: 'uppercase',
  letterSpacing: 0.5, display: 'block', marginBottom: 5,
};
const col2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const sectionTitle = (color: string, icon: string, text: string) => (
  <div style={{ fontSize: 11, color, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, borderBottom: '1px solid #252B45', paddingBottom: 6 }}>
    {icon} {text}
  </div>
);

const FALLBACK: Membro[] = [
  { nome: 'Agnelo Penelas',            email: 'agnelo.pdc@chk.co.ao' },
  { nome: 'Artur Brito',               email: 'artur.pb@chk.co.ao' },
  { nome: 'Bertini BM. Morais',        email: 'Bertini.Morais@chk.co.ao' },
  { nome: 'Cadimiel Pedro',            email: 'cadimiel.p@chk.co.ao' },
  { nome: 'Delcio Cristovao',          email: 'delcio.isc@chk.co.ao' },
  { nome: 'Erica Gamboa',              email: 'erica.ptg@chk.co.ao' },
  { nome: 'Fernando Jaime',            email: 'fernando.jj@chk.co.ao' },
  { nome: 'Helio Dias Brito Teixeira', email: 'helio.dbt@chk.co.ao' },
  { nome: 'Lauro Monteiro',            email: 'lauro.sjm@chk.co.ao' },
  { nome: 'Manuel Miguel',             email: 'manuel.tm@chk.co.ao' },
  { nome: 'Renato Camenha',            email: 'renato.bc@chk.co.ao' },
  { nome: 'Silvio Makemba',            email: 'silvio.m@chk.co.ao' },
];

export const NovaOportunidadeModal: React.FC<Props> = ({
  currentUserEmail, currentUserName, onSubmit, onClose, getMembers, getNextCode
}) => {
  const [membros, setMembros] = useState<Membro[]>([]);
  const [loadingMembros, setLoadingMembros] = useState(true);
  const [loadingCode, setLoadingCode] = useState(true);
  const isDirtyRef = useRef(false);

  const defaultPrazo = new Date(Date.now() + 7 * 86400000).toISOString().substring(0, 10);

  const [form, setForm] = useState<NovaOportunidadeForm>({
    titulo: '',
    cliente: '',
    contactoCliente: '',
    comercialEmail: currentUserEmail,
    comercialNome: currentUserName,
    statusOportunidade: '2. Levantamento de Necessidades' as OpportunityStatus,
    ambitoNecessidades: '',
    tecnicoEmail: '',
    tecnicoNome: '',
    statusTecnico: 'Não Iniciado' as TechnicalStatus,
    dataLimiteParecer: defaultPrazo,
    valorEstimado: undefined,
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Guard: warn before closing with unsaved data
  const handleClose = () => {
    if (isDirtyRef.current && (form.cliente || form.ambitoNecessidades)) {
      if (!window.confirm('Tens dados por guardar. Tens a certeza que queres fechar?')) return;
    }
    onClose();
  };

  useEffect(() => {
    getNextCode()
      .then(code => setForm(f => ({ ...f, titulo: code })))
      .catch(() => {})
      .finally(() => setLoadingCode(false));
  }, []);

  useEffect(() => {
    getMembers()
      .then(m => {
        const lista = m && m.length > 0 ? m : FALLBACK;
        setMembros(lista);
        const eu = lista.find(x => x.email.toLowerCase() === currentUserEmail.toLowerCase());
        if (eu) setForm(f => ({ ...f, comercialEmail: eu.email, comercialNome: eu.nome }));
      })
      .catch(() => {
        setMembros(FALLBACK);
        const eu = FALLBACK.find(x => x.email.toLowerCase() === currentUserEmail.toLowerCase());
        if (eu) setForm(f => ({ ...f, comercialEmail: eu.email, comercialNome: eu.nome }));
      })
      .finally(() => setLoadingMembros(false));
  }, []);

  const set = (k: keyof NovaOportunidadeForm, v: any) => {
    isDirtyRef.current = true;
    setForm(f => ({ ...f, [k]: v }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.titulo.trim()) e.titulo = 'Obrigatório';
    if (!form.cliente.trim()) e.cliente = 'Obrigatório';
    if (!form.comercialEmail) e.comercialEmail = 'Obrigatório';
    if (!form.ambitoNecessidades.trim()) e.ambitoNecessidades = 'Obrigatório';
    if (!form.tecnicoEmail) e.tecnicoEmail = 'Obrigatório';
    if (!form.dataLimiteParecer) e.dataLimiteParecer = 'Obrigatório';
    // Validação: data limite não pode ser no passado
    if (form.dataLimiteParecer && new Date(form.dataLimiteParecer) < new Date(new Date().toDateString())) {
      e.dataLimiteParecer = 'Data não pode ser no passado';
    }
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setSubmitting(true);
    try {
      await onSubmit({
        ...form,
        statusOportunidade: '2. Levantamento de Necessidades' as OpportunityStatus,
        statusTecnico: 'Não Iniciado' as TechnicalStatus,
      });
      isDirtyRef.current = false;
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const borderErr = (k: string) => ({ ...inputStyle, borderColor: errors[k] ? '#EF4444' : '#252B45' });

  const MemberSelect = ({ field, nomField }: { field: 'comercialEmail' | 'tecnicoEmail'; nomField: 'comercialNome' | 'tecnicoNome' }) => (
    <select
      value={form[field]}
      disabled={loadingMembros}
      onChange={e => {
        const m = membros.find(x => x.email === e.target.value);
        set(field, e.target.value);
        if (m) set(nomField, m.nome);
      }}
      style={{ ...borderErr(field), opacity: loadingMembros ? 0.5 : 1 }}
    >
      <option value="">{loadingMembros ? 'A carregar...' : '-- Seleccionar --'}</option>
      {membros.map(m => <option key={m.email} value={m.email}>{m.nome}</option>)}
    </select>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflowY: 'auto',
    }} onClick={e => e.target === e.currentTarget && handleClose()}>
      <div style={{
        background: '#1A1F35', border: '1px solid #252B45', borderRadius: 16,
        width: '100%', maxWidth: 660, maxHeight: '92vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #252B45', background: '#131729', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>➕ Nova Oportunidade</div>
            <div style={{ fontSize: 12, color: '#8892AA', marginTop: 2 }}>Preenche todos os campos obrigatórios (*)</div>
          </div>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', color: '#8892AA', fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 22px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {sectionTitle('#CC2936', '📋', 'Identificação')}
          <div style={col2}>
            <div>
              <label style={labelStyle}>Código da Oportunidade</label>
              <div style={{ position: 'relative' }}>
                <input
                  style={{ ...inputStyle, borderColor: '#252B45', opacity: loadingCode ? 0.5 : 1, paddingRight: 32 }}
                  value={loadingCode ? 'A gerar...' : form.titulo}
                  readOnly
                />
                {!loadingCode && (
                  <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#22C55E' }}>✓ Auto</span>
                )}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Data Limite Parecer *</label>
              <input
                type="date"
                value={form.dataLimiteParecer}
                min={new Date().toISOString().substring(0, 10)}
                onChange={e => set('dataLimiteParecer', e.target.value)}
                style={{ ...borderErr('dataLimiteParecer'), colorScheme: 'dark' }}
              />
              {errors.dataLimiteParecer && <div style={{ fontSize: 11, color: '#EF4444', marginTop: 3 }}>{errors.dataLimiteParecer}</div>}
            </div>
          </div>

          <div style={col2}>
            <div>
              <label style={labelStyle}>Cliente *</label>
              <input style={borderErr('cliente')} value={form.cliente} onChange={e => set('cliente', e.target.value)} placeholder="Nome da empresa" />
              {errors.cliente && <div style={{ fontSize: 11, color: '#EF4444', marginTop: 3 }}>{errors.cliente}</div>}
            </div>
            <div>
              <label style={labelStyle}>Email / Contacto do Cliente</label>
              <input style={inputStyle} value={form.contactoCliente} onChange={e => set('contactoCliente', e.target.value)} placeholder="contacto@cliente.com" />
            </div>
          </div>

          {sectionTitle('#F59E0B', '💰', 'Valor Estimado')}
          <div style={col2}>
            <div>
              <label style={labelStyle}>Valor Estimado (USD) — opcional</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#8892AA', fontSize: 13 }}>$</span>
                <input
                  type="number"
                  min={0}
                  step={100}
                  style={{ ...inputStyle, paddingLeft: 24 }}
                  value={form.valorEstimado ?? ''}
                  onChange={e => set('valorEstimado', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="0"
                />
              </div>
              <div style={{ fontSize: 10, color: '#3A3F55', marginTop: 4 }}>Usado para métricas de pipeline</div>
            </div>
            <div />
          </div>

          {sectionTitle('#CC2936', '📝', 'Âmbito e Necessidades')}
          <div>
            <label style={labelStyle}>Descrição do Âmbito *</label>
            <textarea
              value={form.ambitoNecessidades}
              onChange={e => set('ambitoNecessidades', e.target.value)}
              rows={4}
              placeholder="Descreve detalhadamente o que o cliente precisa..."
              style={{ ...borderErr('ambitoNecessidades'), resize: 'vertical' }}
            />
            {errors.ambitoNecessidades && <div style={{ fontSize: 11, color: '#EF4444', marginTop: 3 }}>{errors.ambitoNecessidades}</div>}
          </div>

          {sectionTitle('#1755B8', '💼', 'Responsável Comercial')}
          <div style={col2}>
            <div>
              <label style={labelStyle}>Comercial *</label>
              <MemberSelect field="comercialEmail" nomField="comercialNome" />
              {errors.comercialEmail && <div style={{ fontSize: 11, color: '#EF4444', marginTop: 3 }}>{errors.comercialEmail}</div>}
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input style={{ ...inputStyle, opacity: .5 }} value={form.comercialEmail} readOnly />
            </div>
          </div>

          {sectionTitle('#22C55E', '🔧', 'Responsável Técnico (DTI)')}
          <div style={col2}>
            <div>
              <label style={labelStyle}>Técnico Responsável *</label>
              <MemberSelect field="tecnicoEmail" nomField="tecnicoNome" />
              {errors.tecnicoEmail && <div style={{ fontSize: 11, color: '#EF4444', marginTop: 3 }}>{errors.tecnicoEmail}</div>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <label style={labelStyle}>Email Técnico</label>
              <input style={{ ...inputStyle, opacity: .5 }} value={form.tecnicoEmail} readOnly />
            </div>
          </div>

          {/* Preview */}
          {form.cliente && (
            <div style={{ background: 'rgba(204,41,54,.06)', border: '1px solid rgba(204,41,54,.2)', borderRadius: 10, padding: '12px 16px' }}>
              <div style={{ fontSize: 11, color: '#CC2936', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>Pré-visualização</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{form.titulo || '—'}</div>
              <div style={{ fontSize: 12, color: '#8892AA', marginTop: 3 }}>
                🏢 {form.cliente}
                {form.valorEstimado ? ` · 💰 $${form.valorEstimado.toLocaleString('pt-PT')}` : ''}
                {' · '}Técnico: {form.tecnicoNome || '—'}
                {' · '}Prazo: {form.dataLimiteParecer || '—'}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid #252B45', display: 'flex', gap: 10, justifyContent: 'flex-end', background: 'rgba(0,0,0,.2)', flexShrink: 0 }}>
          <button onClick={handleClose} style={{ background: '#131729', border: '1px solid #252B45', color: '#8892AA', borderRadius: 8, padding: '10px 20px', fontSize: 13, cursor: 'pointer' }}>
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              background: submitting ? '#555' : 'linear-gradient(135deg,#CC2936,#9B1D24)',
              color: 'white', border: 'none', borderRadius: 8, padding: '10px 26px',
              fontSize: 13, fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? '⏳ A criar...' : '✅ Criar Oportunidade'}
          </button>
        </div>
      </div>
    </div>
  );
};
